import {
  SerializedConstructor,
  SerializedSecret,
} from '@langchain/core/load/serializable';
import { Button, FormControl, Input } from '@mui/joy';

import { ReactNode } from 'react';

import XIcon from '@/icons/XIcon.svg?react';

import { PlaygroundSecretInput } from '../../../PlaygroundSecretInput';
import { getStorageSecretKey } from '../../../PlaygroundSecrets.utils';
import { ManifestEditorProps, ManifestSecretProps } from '../../Manifest.utils';

export const ManifestSecretOpenAI = (props: ManifestSecretProps) => {
  return (
    <div className="flex flex-col gap-1">
      <PlaygroundSecretInput
        name="OPENAI_API_KEY"
        value={props.value['OPENAI_API_KEY'] ?? ''}
        onChange={(value) =>
          props.onChange({
            ...props.value,
            ['OPENAI_API_KEY']: value,
          })
        }
      />

      <p className="text-sm text-tertiary">
        Don't have a key? Get one{' '}
        <a
          href="https://platform.openai.com/account/api-keys"
          target="_blank"
          rel="noreferer noopener"
          className="text-brand-green-400 hover:underline active:underline"
        >
          here
        </a>
        .
      </p>
    </div>
  );
};

const SettingsSecretBindings: Record<
  string,
  {
    title: ReactNode;
    prepare: (clone: SerializedConstructor) => SerializedConstructor;
    cleanup: (clone: SerializedConstructor) => SerializedConstructor;
    Component?: (
      props: ManifestEditorProps<SerializedConstructor>
    ) => ReactNode;
  }
> = {
  openai: {
    title: 'Switch to OpenAI',
    prepare(clone) {
      const newValue = JSON.parse(
        JSON.stringify(clone)
      ) as SerializedConstructor;
      newValue.kwargs['openai_api_key'] = {
        lc: 1,
        type: 'secret',
        id: ['OPENAI_API_KEY'],
      } satisfies SerializedSecret;

      // Remove both base_url and openai_api_base when switching to OpenAI
      delete newValue.kwargs['base_url'];
      delete newValue.kwargs['openai_api_base'];

      return newValue;
    },
    cleanup: (clone) => {
      const newValue = JSON.parse(
        JSON.stringify(clone)
      ) as SerializedConstructor;
      // Remove both aliases when cleaning up
      delete newValue.kwargs['base_url'];
      delete newValue.kwargs['openai_api_base'];
      return newValue;
    },
  },
  proxy: {
    title: 'Use Proxy Provider',
    prepare: (clone) => {
      const newValue = JSON.parse(
        JSON.stringify(clone)
      ) as SerializedConstructor;
      const baseURL = window.localStorage.getItem(
        getStorageSecretKey('openai_base_url')
      );

      newValue.kwargs['openai_api_base'] ??= baseURL;
      return newValue;
    },
    cleanup: (clone) => {
      const newValue = JSON.parse(
        JSON.stringify(clone)
      ) as SerializedConstructor;
      // Remove both aliases when cleaning up
      delete newValue.kwargs['base_url'];
      delete newValue.kwargs['openai_api_base'];
      return newValue;
    },
    Component: (props) => {
      return (
        <FormControl>
          <Input
            type="text"
            value={
              props.value.kwargs.base_url ??
              props.value.kwargs.openai_api_base ??
              ''
            }
            placeholder="Base URL"
            onChange={(e) => {
              window.localStorage.setItem(
                getStorageSecretKey('openai_base_url'),
                e.target.value
              );

              props.onChange({
                ...props.value,
                kwargs: {
                  ...props.value.kwargs,
                  base_url: e.target.value,
                },
              });
            }}
            size="sm"
            sx={{
              '& input': { boxShadow: 'none !important' },

              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          />
        </FormControl>
      );
    },
  },
};

export function ManifestCommonOpenAIProviderType(
  props: ManifestEditorProps<SerializedConstructor>
) {
  let current: keyof typeof SettingsSecretBindings = 'openai';

  if ('base_url' in props.value.kwargs) {
    current = 'proxy';
  }

  if ('openai_api_base' in props.value.kwargs) {
    current = 'proxy';
  }

  const currentBinding = SettingsSecretBindings[current];
  const SpecificSecretType = currentBinding.Component;

  const handleProviderChange = () => {
    try {
      // Apply cleanup function for current provider and get a clean copy
      const cleanedValue = SettingsSecretBindings[current].cleanup(props.value);

      // Then apply prepare function for the new provider
      const nextKey = current === 'openai' ? 'proxy' : 'openai';
      const preparedValue =
        SettingsSecretBindings[nextKey].prepare(cleanedValue);

      // Update the parent component with the new value
      props.onChange(preparedValue);
    } catch (error) {
      console.error('Error switching providers:', error);
    }
  };

  return (
    <div className="flex items-start gap-0">
      <div className="flex flex-wrap items-center gap-1">
        {Object.keys(SettingsSecretBindings)
          .filter((next) => next !== current)
          .map((value) => {
            const next = value as keyof typeof SettingsSecretBindings;
            return (
              <Button
                key={next}
                size="sm"
                variant="outlined"
                color="neutral"
                sx={{
                  whiteSpace: 'nowrap',
                  ...(SettingsSecretBindings[next].title !==
                    'Use Proxy Provider' && {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderRightWidth: 0,
                  }),
                }}
                onClick={handleProviderChange}
              >
                {SettingsSecretBindings[next].title}
              </Button>
            );
          })}
      </div>
      {SpecificSecretType != null && <SpecificSecretType {...props} />}
    </div>
  );
}

export function ManifestOpenAISeed(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const value =
    typeof props.options?.seed === 'number' ? props.options?.seed : null;

  return (
    <FormControl orientation="horizontal">
      <span className="my-auto mr-2 text-sm font-medium">Seed</span>

      <Input
        type="number"
        size="sm"
        sx={{
          width: 100,
          '& input': {
            textAlign: 'center',
            boxShadow: 'none !important',
            MozAppearance: 'textfield',
          },
          '& input::-webkit-outer-spin-button': {
            WebkitAppearance: 'none !important',
            margin: 0,
          },
          '& input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none !important',
            margin: 0,
          },
        }}
        value={value ?? ''}
        readOnly={props.readOnly}
        onChange={(e) => {
          props.onOptionsChange({
            ...props.options,
            seed: parseInt(e.target.value),
          });
        }}
        endDecorator={
          !props.readOnly &&
          value != null && (
            <button
              type="button"
              className="rounded-md p-0.5 text-tertiary hover:bg-secondary-hover active:bg-secondary"
              aria-label="Clear"
              onClick={() => {
                props.onOptionsChange({
                  ...props.options,
                  seed: undefined,
                });
              }}
            >
              <XIcon className="h-5 w-5" />
            </button>
          )
        }
      />
    </FormControl>
  );
}
